import type { Price } from '@misumi/shared'
import { useId, useState } from 'react'
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  DownOutlined,
  FileTextOutlined,
  IconCustomSparkle,
  IconDragHandle,
  IconLayers,
  IconStandardBox,
  LockOutlined,
  RightOutlined,
} from '../../../components/icons'
import { Button } from '../../../components/ui/Button'
import { Chip } from '../../../components/ui/Chip'
import { formatPrice, formatSubtotal } from '../format'
import type { BomLineItem } from '../types'

export interface AssemblyTreeProps {
  readonly lines: readonly BomLineItem[]
  readonly selectedIds: ReadonlySet<string>
  /** The assembly/group row's tri-state checkbox — always a bulk toggle of
   * every descendant leaf, never a shift-click range gesture (same rule
   * `StagingBomPartsTree`'s group/header checkbox follows, ADR-0142 D7(1):
   * "a group/header checkbox toggle always passes false"). */
  readonly onToggleSelect: (lineIds: readonly string[], select: boolean) => void
  /** UID-610 — the LEAF row's own checkbox click handler. `shiftKey` carries
   * the modifier state from the DOM `click` event (a mouse click AND a
   * Space-key activation of a focused checkbox both dispatch one, per
   * `PartsTable.tsx`'s precedent) — the caller resolves it into a Gmail/
   * Finder range select over the tree's currently VISIBLE leaves (see
   * `collapsedIds` below) via the shared `useRangeSelection` hook. */
  readonly onToggleSelectLeaf: (lineId: string, shiftKey: boolean) => void
  /** UID-610 — ids of assemblies whose children are currently hidden. Lifted
   * out of this component (previously a local `useState` per row) so the
   * caller can compute which leaves are actually rendered right now, and
   * exclude a collapsed assembly's leaves from a shift-click range the same
   * way a filtered-out row already is elsewhere in the app. */
  readonly collapsedIds: ReadonlySet<string>
  readonly onToggleExpand: (lineId: string) => void
  readonly onDelete: (lineId: string) => void
  readonly onMove: (parentLineId: string | null, lineId: string, direction: 'up' | 'down') => void
  readonly onAddToCart: (lineId: string) => void
  /** UID-295/296 — per-line "Request quote" (measured spec §B4) for a
   * `quote_required` leaf. Optional so existing callers/tests that predate
   * this (and don't have a per-line RFQ queue path) still render the
   * button in its disabled state. mi8 (code-review fix, QA fix round)
   * collapsed what used to be a separate link + button pair into this one
   * canonical control. */
  readonly onRequestQuote?: (lineId: string) => void
  /** UID-295/296 — the editable Qty cell (measured spec §B4: 52×32 input).
   * NOT wired to a persistence call by this component — `PATCH
   * /boms/:bomId/lines/:lineId` exists in the API contract but
   * `BomDetailService`/`useBomDetail.ts` (the frozen data-layer contract for
   * this ticket) doesn't yet expose a method for it. Optional: the caller
   * decides what happens with an edited value (today, `BomDetailPage` shows
   * a "not wired up yet" toast rather than silently discarding it — see that
   * file's comment). Omitted renders the qty as plain text, same as before. */
  readonly onQuantityChange?: (lineId: string, quantity: number) => void
  /** UID-295/296 — same-parent drag-and-drop reorder (measured spec's drag
   * handle), in ADDITION to (never a replacement for) the keyboard-operable
   * Move up/down buttons below (accessibility-wcag: drag alone excludes
   * keyboard/screen-reader users). Optional — omitted disables the
   * draggable affordance but keeps every other interaction. */
  readonly onReorderDrop?: (
    parentLineId: string | null,
    draggedId: string,
    targetId: string,
  ) => void
}

function collectLeafIds(node: BomLineItem): readonly string[] {
  if (!node.children || node.children.length === 0) return [node.lineId]
  return node.children.flatMap(collectLeafIds)
}

/** Exported for `BomDetailPage`'s table header "select all" checkbox and the
 * footer Total row's leaf-count, so both compute the SAME leaf-id set this
 * tree's own per-node selection does. */
export function collectAllLeafIds(lines: readonly BomLineItem[]): readonly string[] {
  return lines.flatMap(collectLeafIds)
}

function collectVisibleLeafIds(
  node: BomLineItem,
  collapsedIds: ReadonlySet<string>,
): readonly string[] {
  if (!node.children || node.children.length === 0) return [node.lineId]
  if (collapsedIds.has(node.lineId)) return []
  return node.children.flatMap((child) => collectVisibleLeafIds(child, collapsedIds))
}

/**
 * UID-610 — the rendered-order leaf-id list a shift-click range is computed
 * over: same leaves as {@link collectAllLeafIds}, minus anything hidden
 * inside a collapsed assembly. Exported for `BomDetailPage`, which owns
 * `collapsedIds` and threads it into `useRangeSelection.toggle`'s `ids`
 * argument.
 */
export function collectVisibleLeafIdsForRange(
  lines: readonly BomLineItem[],
  collapsedIds: ReadonlySet<string>,
): readonly string[] {
  return lines.flatMap((line) => collectVisibleLeafIds(line, collapsedIds))
}

/** Rolled-up quantity × price across every priced leaf under `line`
 * (measured spec §B4: assembly/sub-assembly rows show a rolled-up
 * qty+subtotal, not a blank cell). Returns `null` when no descendant leaf
 * has a numeric price yet, so the caller can render the same em-dash
 * `formatSubtotal` uses for an unpriced leaf. */
function sumChildSubtotal(line: BomLineItem): Price | null {
  if (!line.children) return null
  let total = 0
  let currency: string | null = null
  let any = false
  for (const child of line.children) {
    if (child.kind === 'part' && child.sourceable?.indicativePrice) {
      const price = child.sourceable.indicativePrice
      const amount = Number.parseFloat(price.amount)
      if (Number.isFinite(amount)) {
        total += amount * child.quantity
        currency = price.currency
        any = true
      }
    } else if (child.kind === 'assembly') {
      const childTotal = sumChildSubtotal(child)
      if (childTotal) {
        const amount = Number.parseFloat(childTotal.amount)
        if (Number.isFinite(amount)) {
          total += amount
          currency = childTotal.currency
          any = true
        }
      }
    }
  }
  if (!any || !currency) return null
  return { amount: total.toFixed(2), currency } as Price
}

/**
 * The BOM assembly tree (components.md "Assembly tree (BOM)", measured
 * design spec §B4) — a semantic `role="tree"` of expandable assemblies and
 * leaf parts, each with a tri-state group checkbox, drag-handle-equivalent
 * Move up/down controls (kept for keyboard/screen-reader users per
 * accessibility-wcag — drag is an ADDITIONAL affordance, never a
 * replacement), and a Remove action. `aria-level` is set per depth so
 * indentation is never the ONLY signal of hierarchy for assistive tech
 * (accessibility-wcag / the measured spec's B4 a11y notes).
 */
export function AssemblyTree({
  lines,
  selectedIds,
  onToggleSelect,
  onToggleSelectLeaf,
  collapsedIds,
  onToggleExpand,
  onDelete,
  onMove,
  onAddToCart,
  onRequestQuote,
  onQuantityChange,
  onReorderDrop,
}: AssemblyTreeProps) {
  return (
    <ul role="tree" aria-label="Assembly tree" className="assembly-tree">
      {lines.map((line, index) => (
        <TreeNode
          key={line.lineId}
          line={line}
          parentLineId={null}
          siblingIds={lines.map((l) => l.lineId)}
          index={index}
          depth={0}
          selectedIds={selectedIds}
          onToggleSelect={onToggleSelect}
          onToggleSelectLeaf={onToggleSelectLeaf}
          collapsedIds={collapsedIds}
          onToggleExpand={onToggleExpand}
          onDelete={onDelete}
          onMove={onMove}
          onAddToCart={onAddToCart}
          onRequestQuote={onRequestQuote}
          onQuantityChange={onQuantityChange}
          onReorderDrop={onReorderDrop}
        />
      ))}
    </ul>
  )
}

function TreeNode({
  line,
  parentLineId,
  siblingIds,
  index,
  depth,
  selectedIds,
  onToggleSelect,
  onToggleSelectLeaf,
  collapsedIds,
  onToggleExpand,
  onDelete,
  onMove,
  onAddToCart,
  onRequestQuote,
  onQuantityChange,
  onReorderDrop,
}: {
  readonly line: BomLineItem
  readonly parentLineId: string | null
  readonly siblingIds: readonly string[]
  readonly index: number
  readonly depth: number
  readonly selectedIds: ReadonlySet<string>
  readonly onToggleSelect: (lineIds: readonly string[], select: boolean) => void
  readonly onToggleSelectLeaf: (lineId: string, shiftKey: boolean) => void
  readonly collapsedIds: ReadonlySet<string>
  readonly onToggleExpand: (lineId: string) => void
  readonly onDelete: (lineId: string) => void
  readonly onMove: (parentLineId: string | null, lineId: string, direction: 'up' | 'down') => void
  readonly onAddToCart: (lineId: string) => void
  readonly onRequestQuote?: ((lineId: string) => void) | undefined
  readonly onQuantityChange?: ((lineId: string, quantity: number) => void) | undefined
  readonly onReorderDrop?:
    | ((parentLineId: string | null, draggedId: string, targetId: string) => void)
    | undefined
}) {
  const expanded = !collapsedIds.has(line.lineId)
  const [qtyDraft, setQtyDraft] = useState(String(line.quantity))
  const [qtyError, setQtyError] = useState<string | undefined>(undefined)
  const qtyErrorId = useId()
  const isAssembly = line.kind === 'assembly'
  const leafIds = collectLeafIds(line)
  const allSelected = leafIds.every((id) => selectedIds.has(id))
  const someSelected = !allSelected && leafIds.some((id) => selectedIds.has(id))
  const isCustom = line.sourceable?.provenance === 'fictiv_custom'
  const pricingMode = line.sourceable?.pricingProjection?.mode
  const isQuoteRequired = pricingMode === 'quote_required'
  const isLocked = pricingMode === 'locked_to_rfq'
  // Measured spec §B4: root assembly (depth 0) and sub-assembly (depth 1+)
  // group rows get DISTINCT tints — see tokens.css's --color-bom-row-*
  // (flagged for design sign-off).
  const rowToneClass = isAssembly
    ? depth === 0
      ? 'assembly-tree-row--assembly-root'
      : 'assembly-tree-row--assembly-sub'
    : ''

  // UID-296 item 4 (QA fix round) — the old check
  // (`Number.parseInt(qtyDraft, 10)`) silently accepted "2.5" as 2
  // (`parseInt` truncates rather than rejects) and silently reverted 0/
  // negative/non-numeric input with NO visible feedback — a user could type
  // "0" or "-3" or "2.5", tab away, and watch their input vanish with no
  // explanation. This now validates the RAW string against "one or more
  // digits" (rejects decimals, negatives, and non-numeric text alike),
  // surfaces an inline, accessible (`role="alert"`, `aria-describedby`)
  // error message instead of silently reverting, and never calls
  // `onQuantityChange` for an invalid value.
  function commitQty() {
    const trimmed = qtyDraft.trim()
    const isValidWholeNumber = /^\d+$/.test(trimmed) && Number.parseInt(trimmed, 10) > 0
    if (!isValidWholeNumber) {
      setQtyError('Enter a whole number greater than 0.')
      return
    }
    setQtyError(undefined)
    const next = Number.parseInt(trimmed, 10)
    if (next !== line.quantity && onQuantityChange) {
      onQuantityChange(line.lineId, next)
    }
  }

  return (
    <li
      role="treeitem"
      aria-expanded={isAssembly ? expanded : undefined}
      aria-level={depth + 1}
      className="assembly-tree-item"
    >
      <div
        className={`assembly-tree-row ${rowToneClass}`}
        draggable={Boolean(onReorderDrop)}
        onDragStart={(event) => {
          event.dataTransfer.setData('text/plain', line.lineId)
        }}
        onDragOver={(event) => {
          if (onReorderDrop) event.preventDefault()
        }}
        onDrop={(event) => {
          event.preventDefault()
          const draggedId = event.dataTransfer.getData('text/plain')
          if (draggedId && draggedId !== line.lineId) {
            onReorderDrop?.(parentLineId, draggedId, line.lineId)
          }
        }}
      >
        {onReorderDrop && (
          <span className="assembly-tree-drag-handle" aria-hidden="true">
            <IconDragHandle />
          </span>
        )}
        {isAssembly ? (
          <button
            type="button"
            className="assembly-tree-toggle icon-btn"
            aria-label={expanded ? `Collapse ${line.name}` : `Expand ${line.name}`}
            onClick={() => onToggleExpand(line.lineId)}
          >
            {expanded ? <DownOutlined aria-hidden="true" /> : <RightOutlined aria-hidden="true" />}
          </button>
        ) : (
          <span className="assembly-tree-toggle-spacer" aria-hidden="true" />
        )}
        <input
          type="checkbox"
          aria-label={`Select ${line.name}`}
          checked={allSelected}
          ref={(el) => {
            if (el) el.indeterminate = someSelected
          }}
          // UID-610 — a LEAF row's checkbox is a single shift-click-rangeable
          // row, so it reads `event.shiftKey` off `onClick` (mouse AND the
          // Space-key equivalent, see `onToggleSelectLeaf`'s doc comment) and
          // leaves `onChange` a no-op. An ASSEMBLY row's checkbox is a bulk
          // toggle of every descendant leaf at once — never a single anchor
          // a range can be drawn to/from — so it keeps the plain `onChange`
          // toggle it always had.
          onClick={
            isAssembly ? undefined : (event) => onToggleSelectLeaf(line.lineId, event.shiftKey)
          }
          onChange={
            isAssembly ? (event) => onToggleSelect(leafIds, event.target.checked) : () => {}
          }
        />
        {isAssembly && (
          <span className="assembly-tree-layers-icon">
            <IconLayers />
          </span>
        )}
        {!isAssembly && line.thumbnailUrl && (
          <img
            src={line.thumbnailUrl}
            alt=""
            className="assembly-tree-thumb"
            width={34}
            height={34}
          />
        )}
        <span className="assembly-tree-name-block">
          <span className="assembly-tree-name">
            {line.name}
            {line.revision && (
              <span className="caption-text">
                {' '}
                rev {line.revision}
                {isAssembly &&
                  // UID-296 AC2 (QA fix round) — the prototype's group-row
                  // caption reads "Rev {rev} · {N} builds", but no "builds"
                  // count exists on `BomLineItem` (the frozen data contract)
                  // or anywhere in the API contract yet — flagged here
                  // rather than invented. Until a real backend field lands,
                  // this appends the rolled-up LEAF PART count (the same
                  // `collectLeafIds` set this row already computes for its
                  // own tri-state checkbox), which is real data.
                  ` · ${leafIds.length} part${leafIds.length === 1 ? '' : 's'}`}
              </span>
            )}
          </span>
          {!isAssembly && (line.mpn || line.descriptionLink) && (
            <span className="caption-text assembly-tree-mpn">
              {[line.mpn, line.descriptionLink].filter(Boolean).join(' · ')}
            </span>
          )}
          {!isAssembly && line.datasheetUrl && (
            <a
              href={line.datasheetUrl}
              target="_blank"
              rel="noreferrer"
              className="assembly-tree-datasheet-link"
            >
              <FileTextOutlined aria-hidden="true" />
              datasheet.pdf
            </a>
          )}
        </span>
        {/* MA3 (code-review fix, QA fix round) — this is a visual "table"
            (`<div>`/`<span>` header cells over this `role="tree"`, see
            BomDetailPage.tsx's header comment), so a screen-reader user
            landing on a data cell doesn't get its column header for free
            (WCAG 1.3.1). Minimum fix: each data cell speaks its own label
            via `aria-label`, without changing what's visibly rendered. A
            full `role="treegrid"` (real per-cell header association) is a
            bigger structural change — left as a follow-up, not attempted
            here. */}
        {/* UID-379 (V10) — category (bold, line 1) stacked over lead time
            (caption, line 2) via flex-column, replacing the single
            ` · `-joined span. The combined `aria-label` is kept verbatim
            (accessibility-wcag / MA3's existing "each data cell speaks its
            own label" fix) — a screen-reader user still hears one
            "Requirements: X · Y" announcement, not two separate cells. */}
        {!isAssembly && (
          <span
            className="assembly-tree-requirements"
            aria-label={`Requirements: ${[line.category, line.leadTime].filter(Boolean).join(' · ') || 'none'}`}
          >
            {line.category || line.leadTime ? (
              <>
                {line.category && (
                  <span className="assembly-tree-requirements-category" aria-hidden="true">
                    {line.category}
                  </span>
                )}
                {line.leadTime && (
                  <span
                    className="caption-text assembly-tree-requirements-leadtime"
                    aria-hidden="true"
                  >
                    {line.leadTime}
                  </span>
                )}
              </>
            ) : (
              <span className="caption-text" aria-hidden="true">
                —
              </span>
            )}
          </span>
        )}
        {!isAssembly && (
          <Chip
            tone={isCustom ? 'brand' : 'neutral'}
            icon={isCustom ? <IconCustomSparkle /> : <IconStandardBox />}
            className="assembly-tree-source-chip"
            aria-label={`Source: ${isCustom ? 'Make' : 'Buy'}`}
          >
            {isCustom ? 'Make' : 'Buy'}
          </Chip>
        )}
        {isAssembly ? (
          <span className="assembly-tree-qty">Qty {line.quantity}</span>
        ) : onQuantityChange ? (
          <label className="assembly-tree-qty-field">
            <span className="sr-only">Quantity for {line.name}</span>
            <input
              type="text"
              inputMode="numeric"
              className={
                qtyError
                  ? 'assembly-tree-qty-input assembly-tree-qty-input--invalid'
                  : 'assembly-tree-qty-input'
              }
              value={qtyDraft}
              aria-invalid={qtyError ? true : undefined}
              aria-describedby={qtyError ? qtyErrorId : undefined}
              onChange={(event) => {
                setQtyDraft(event.target.value)
                if (qtyError) setQtyError(undefined)
              }}
              onBlur={commitQty}
            />
            {qtyError && (
              <span id={qtyErrorId} role="alert" className="assembly-tree-qty-error caption-text">
                {qtyError}
              </span>
            )}
          </label>
        ) : (
          <span className="assembly-tree-qty">Qty {line.quantity}</span>
        )}
        {line.sourceable?.indicativePrice && (
          <span
            className="assembly-tree-price-block"
            aria-label={`Unit price: ${formatPrice(line.sourceable.indicativePrice)} per unit, volume breaks available`}
          >
            <span className="assembly-tree-price" aria-hidden="true">
              {formatPrice(line.sourceable.indicativePrice)}
            </span>
            <span className="caption-text" aria-hidden="true">
              per unit · vol. breaks
            </span>
          </span>
        )}
        {!isAssembly && (
          <span
            className="assembly-tree-subtotal"
            aria-label={`Subtotal: ${formatSubtotal(line.sourceable?.indicativePrice, line.quantity)}`}
          >
            {formatSubtotal(line.sourceable?.indicativePrice, line.quantity)}
          </span>
        )}
        {isAssembly &&
          (() => {
            const rollup = sumChildSubtotal(line)
            const display = rollup ? formatPrice(rollup) : '—'
            return (
              <span className="assembly-tree-subtotal" aria-label={`Subtotal: ${display}`}>
                {display}
              </span>
            )
          })()}
        {isQuoteRequired && (
          <span className="assembly-tree-quote-actions">
            <Chip tone="violet">Quote required</Chip>
            {/* mi8 (code-review fix, QA fix round) — this used to render
                BOTH an always-live `<a href="/rfq/new">` (faked "disabled"
                via `preventDefault`, which isn't a real disabled state and
                isn't announced as one) AND a `disabled`-capable `<Button>`
                for the SAME action — two tab stops for one control, with
                inconsistent disabled semantics between them. Now there's
                ONE canonical control: a real `<button>` with a genuine
                `disabled` attribute when `onRequestQuote` isn't wired up. */}
            <Button
              variant="outline-purple"
              size="sm"
              className="bom-quote-cta"
              onClick={() => onRequestQuote?.(line.lineId)}
              disabled={!onRequestQuote}
            >
              Request quote
            </Button>
          </span>
        )}
        {isLocked && (
          <Chip tone="warning" icon={<LockOutlined aria-hidden="true" />}>
            Locked to RFQ
          </Chip>
        )}
        {!isAssembly && !isQuoteRequired && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAddToCart(line.lineId)}
            disabled={isLocked}
          >
            Add to cart
          </Button>
        )}
        <button
          type="button"
          className="icon-btn"
          aria-label={`Move ${line.name} up`}
          disabled={index === 0}
          onClick={() => onMove(parentLineId, line.lineId, 'up')}
        >
          <ArrowUpOutlined aria-hidden="true" />
        </button>
        <button
          type="button"
          className="icon-btn"
          aria-label={`Move ${line.name} down`}
          disabled={index === siblingIds.length - 1}
          onClick={() => onMove(parentLineId, line.lineId, 'down')}
        >
          <ArrowDownOutlined aria-hidden="true" />
        </button>
        <button
          type="button"
          className="icon-btn assembly-tree-trash"
          aria-label={`Remove ${line.name}`}
          onClick={() => onDelete(line.lineId)}
        >
          <DeleteOutlined aria-hidden="true" />
        </button>
      </div>
      {isAssembly && line.children && line.children.length > 0 && expanded && (
        // This is the WAI-ARIA tree pattern's nested-group role (a
        // treeitem's children), not a form grouping — <fieldset> would be
        // the wrong element here.
        // biome-ignore lint/a11y/useSemanticElements: see comment above.
        <ul role="group" className="assembly-tree-group">
          {line.children.map((child, childIndex) => (
            <TreeNode
              key={child.lineId}
              line={child}
              parentLineId={line.lineId}
              siblingIds={(line.children ?? []).map((c) => c.lineId)}
              index={childIndex}
              depth={depth + 1}
              selectedIds={selectedIds}
              onToggleSelect={onToggleSelect}
              onToggleSelectLeaf={onToggleSelectLeaf}
              collapsedIds={collapsedIds}
              onToggleExpand={onToggleExpand}
              onAddToCart={onAddToCart}
              onDelete={onDelete}
              onMove={onMove}
              onRequestQuote={onRequestQuote}
              onQuantityChange={onQuantityChange}
              onReorderDrop={onReorderDrop}
            />
          ))}
        </ul>
      )}
    </li>
  )
}
